// Ported from the desktop app (ui/src/lib/history.ts).
// Undo/redo over immutable project states (immer gives structural sharing, so storing whole
// states is cheap). Transactions group many edits (a drag, a recording pass) into one step.

export interface History<T> {
  past: T[];
  present: T;
  future: T[];
  /** State at the start of an open transaction, or null. */
  txStart: T | null;
  txLabel: string | null;
}

export const HISTORY_LIMIT = 200;

export function createHistory<T>(initial: T): History<T> {
  return { past: [], present: initial, future: [], txStart: null, txLabel: null };
}

/** Apply a new state. Inside a transaction only `present` changes. */
export function commit<T>(h: History<T>, next: T): History<T> {
  if (next === h.present) return h;
  if (h.txStart !== null) return { ...h, present: next };
  const past = [...h.past, h.present];
  if (past.length > HISTORY_LIMIT) past.shift();
  return { ...h, past, present: next, future: [] };
}

/** Replace the present without creating an undo step (e.g. paths rewritten by Save). */
export function replace<T>(h: History<T>, next: T): History<T> {
  return { ...h, present: next };
}

/** Apply a view-only change (e.g. folded lanes) to every state, so undo/redo never flips it. */
export function mapAll<T>(h: History<T>, fn: (s: T) => T): History<T> {
  return {
    ...h,
    past: h.past.map(fn),
    present: fn(h.present),
    future: h.future.map(fn),
    txStart: h.txStart === null ? null : fn(h.txStart),
  };
}

export function begin<T>(h: History<T>, label: string): History<T> {
  if (h.txStart !== null) return h;
  return { ...h, txStart: h.present, txLabel: label };
}

/** Close the transaction; it becomes one undo step if anything changed. */
export function end<T>(h: History<T>): History<T> {
  if (h.txStart === null) return h;
  const start = h.txStart;
  const base = { ...h, txStart: null, txLabel: null };
  if (start === h.present) return base;
  const past = [...h.past, start];
  if (past.length > HISTORY_LIMIT) past.shift();
  return { ...base, past, future: [] };
}

export function undo<T>(h: History<T>): History<T> {
  const closed = end(h);
  if (closed.past.length === 0) return closed;
  const prev = closed.past[closed.past.length - 1];
  return { ...closed, past: closed.past.slice(0, -1), present: prev, future: [closed.present, ...closed.future] };
}

export function redo<T>(h: History<T>): History<T> {
  if (h.future.length === 0 || h.txStart !== null) return h;
  const [next, ...rest] = h.future;
  return { ...h, past: [...h.past, h.present], present: next, future: rest };
}

export function canUndo<T>(h: History<T>): boolean {
  return h.past.length > 0 || (h.txStart !== null && h.txStart !== h.present);
}

export function canRedo<T>(h: History<T>): boolean {
  return h.future.length > 0;
}
